import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Download, Pencil, Plus, Save, Trash2, Upload, Users, X } from 'lucide-react';
import { useAuthStore } from '../stores/authStore';
import { useLearnersStore } from '../stores/learnersStore';

const SUBJECT_OPTIONS = ['Mathematics', 'Physical Sciences', 'Natural Sciences'];
const SUBJECT_LOOKUP = SUBJECT_OPTIONS.reduce((acc, subject) => {
  acc[subject.toLowerCase()] = subject;
  return acc;
}, {});
const GRADE_OPTIONS = ['4', '5', '6', '7', '8', '9', '10', '11', '12'];

const createEmptyRow = (defaults = {}) => ({
  id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  surname: '',
  name: '',
  grade: '',
  subjects: [],
  school: String(defaults.school || ''),
  project: String(defaults.project || ''),
});

const normalizeGrade = (value) => {
  const parsed = Number.parseInt(String(value || '').trim(), 10);
  if (Number.isNaN(parsed) || parsed < 4 || parsed > 12) return '';
  return String(parsed);
};

const normalizeSubjects = (value) => {
  const list = Array.isArray(value)
    ? value
    : String(value || '')
        .split(/[;,|]/g)
        .map((item) => item.trim())
        .filter(Boolean);

  const normalized = list
    .map((item) => SUBJECT_LOOKUP[String(item).toLowerCase()])
    .filter(Boolean);

  return [...new Set(normalized)];
};

const normalizeLearnerRow = (row, userId) => ({
  userId,
  surname: String(row.surname || '').trim(),
  name: String(row.name || '').trim(),
  grade: normalizeGrade(row.grade),
  subjects: normalizeSubjects(row.subjects),
  school: String(row.school || '').trim(),
  project: String(row.project || '').trim(),
});

const isLearnerRowEmpty = (row) =>
  !String(row.surname || '').trim() &&
  !String(row.name || '').trim() &&
  !String(row.grade || '').trim() &&
  normalizeSubjects(row.subjects).length === 0 &&
  !String(row.school || '').trim() &&
  !String(row.project || '').trim();

const validateLearner = (row) =>
  Boolean(row.surname && row.name && row.grade && row.subjects.length > 0);

const normalizeHeader = (header) => String(header || '').toLowerCase().replace(/[^a-z]/g, '');

const parseCsvText = (content) => {
  const rows = [];
  let row = [];
  let cell = '';
  let inQuotes = false;

  for (let i = 0; i < content.length; i += 1) {
    const char = content[i];
    const next = content[i + 1];

    if (char === '"') {
      if (inQuotes && next === '"') {
        cell += '"';
        i += 1;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }

    if (!inQuotes && (char === '\n' || char === '\r')) {
      if (char === '\r' && next === '\n') i += 1;
      row.push(cell);
      if (row.some((value) => String(value || '').trim() !== '')) rows.push(row);
      row = [];
      cell = '';
      continue;
    }

    if (!inQuotes && char === ',') {
      row.push(cell);
      cell = '';
      continue;
    }

    cell += char;
  }

  row.push(cell);
  if (row.some((value) => String(value || '').trim() !== '')) rows.push(row);
  return rows;
};

const csvRowsToLearners = (rows) => {
  if (!Array.isArray(rows) || rows.length < 2) return [];
  const [headers, ...dataRows] = rows;
  const headerMap = headers.reduce((acc, header, index) => {
    acc[normalizeHeader(header)] = index;
    return acc;
  }, {});

  const getValue = (dataRow, ...keys) => {
    for (const key of keys) {
      const idx = headerMap[key];
      if (typeof idx === 'number' && dataRow[idx] != null) return dataRow[idx];
    }
    return '';
  };

  return dataRows.map((dataRow) => ({
    surname: getValue(dataRow, 'surname'),
    name: getValue(dataRow, 'name'),
    grade: getValue(dataRow, 'grade'),
    subjects: getValue(dataRow, 'subjects', 'subject'),
    school: getValue(dataRow, 'school'),
    project: getValue(dataRow, 'project'),
  }));
};

const getPrefillValuesFromUserData = (userData = {}) => {
  const institutionName = String(userData.institutionName || '').trim();
  const institutionType = String(userData.institutionType || '').trim().toLowerCase();

  const explicitSchool =
    String(userData.school || userData.schoolName || userData.institution || '').trim();
  const explicitProject =
    String(userData.project || userData.projectName || userData.classProject || '').trim();

  return {
    school: explicitSchool || (institutionType !== 'project' ? institutionName : ''),
    project: explicitProject || (institutionType === 'project' ? institutionName : ''),
  };
};

const LearnersPage = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const { user, userData } = useAuthStore();
  const {
    learners,
    isLoading,
    isSaving,
    error,
    loadLearners,
    batchCreateLearners,
    updateLearner,
    deleteLearner,
    clearLearnersError,
  } = useLearnersStore();

  const prefillValues = useMemo(() => getPrefillValuesFromUserData(userData), [userData]);
  const [entryRows, setEntryRows] = useState([createEmptyRow(prefillValues)]);
  const [editingLearnerId, setEditingLearnerId] = useState(null);
  const [editingLearner, setEditingLearner] = useState(null);
  const [statusMessage, setStatusMessage] = useState('');

  useEffect(() => {
    if (!user?.uid) return undefined;
    loadLearners(user.uid).catch((loadError) => {
      console.error('Failed to load learners:', loadError);
    });
    return () => clearLearnersError();
  }, [clearLearnersError, loadLearners, user?.uid]);

  const hasEntryData = useMemo(() => entryRows.some((row) => !isLearnerRowEmpty(row)), [entryRows]);

  const updateEntryRow = (rowId, field, value) => {
    setEntryRows((currentRows) =>
      currentRows.map((row) => {
        if (row.id !== rowId) return row;
        if (field === 'subjects') {
          const subjectValue = String(value);
          const currentSubjects = Array.isArray(row.subjects) ? row.subjects : [];
          const exists = currentSubjects.includes(subjectValue);
          return {
            ...row,
            subjects: exists
              ? currentSubjects.filter((item) => item !== subjectValue)
              : [...currentSubjects, subjectValue],
          };
        }
        return { ...row, [field]: value };
      })
    );
  };

  const removeEntryRow = (rowId) => {
    setEntryRows((currentRows) => {
      if (currentRows.length <= 1) return currentRows;
      return currentRows.filter((row) => row.id !== rowId);
    });
  };

  useEffect(() => {
    setEntryRows((currentRows) =>
      currentRows.map((row) => {
        const nextRow = { ...row };
        if (!String(nextRow.school || '').trim()) nextRow.school = prefillValues.school;
        if (!String(nextRow.project || '').trim()) nextRow.project = prefillValues.project;
        return nextRow;
      })
    );
  }, [prefillValues.project, prefillValues.school]);

  const addEntryRow = () =>
    setEntryRows((currentRows) => [...currentRows, createEmptyRow(prefillValues)]);

  const resetEntryRows = () => setEntryRows([createEmptyRow(prefillValues)]);

  const handleDownloadTemplateCsv = () => {
    const headers = ['Surname', 'Name', 'Grade', 'Subjects', 'School', 'Project'];
    const sampleRow = [
      'Doe',
      'Jane',
      '10',
      'Mathematics;Physical Sciences',
      prefillValues.school || 'Example High School',
      prefillValues.project || 'Term 1',
    ];
    const csvLines = [headers.join(','), sampleRow.map((value) => `"${String(value).replace(/"/g, '""')}"`).join(',')];
    const csvContent = `${csvLines.join('\n')}\n`;

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = 'learners_template.csv';
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
  };

  const handleSaveRows = async () => {
    if (!user?.uid) return;
    setStatusMessage('');

    const normalizedRows = entryRows
      .map((row) => normalizeLearnerRow(row, user.uid))
      .filter((row) => !isLearnerRowEmpty(row));

    if (normalizedRows.length === 0) {
      setStatusMessage('No learner rows to save.');
      return;
    }

    const validRows = normalizedRows.filter(validateLearner);
    const invalidCount = normalizedRows.length - validRows.length;

    if (!validRows.length) {
      setStatusMessage('Rows need Surname, Name, Grade (4-12), and at least one Subject.');
      return;
    }

    try {
      await batchCreateLearners(validRows);
      resetEntryRows();
      setStatusMessage(
        invalidCount > 0
          ? `Saved ${validRows.length} learners. Skipped ${invalidCount} invalid row(s).`
          : `Saved ${validRows.length} learners.`
      );
    } catch (saveError) {
      console.error('Failed to save learner rows:', saveError);
    }
  };

  const handleCsvUpload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || !user?.uid) return;

    setStatusMessage('');
    try {
      const content = await file.text();
      const parsedRows = parseCsvText(content);
      const mappedRows = csvRowsToLearners(parsedRows);
      const normalizedRows = mappedRows
        .map((row) => normalizeLearnerRow(row, user.uid))
        .filter((row) => !isLearnerRowEmpty(row));

      if (!normalizedRows.length) {
        setStatusMessage('CSV does not contain learner rows.');
        return;
      }

      const validRows = normalizedRows.filter(validateLearner);
      const invalidCount = normalizedRows.length - validRows.length;

      if (!validRows.length) {
        setStatusMessage('CSV rows are missing required values.');
        return;
      }

      await batchCreateLearners(validRows);
      setStatusMessage(
        invalidCount > 0
          ? `Uploaded ${validRows.length} learners. Skipped ${invalidCount} invalid row(s).`
          : `Uploaded ${validRows.length} learners.`
      );
    } catch (csvError) {
      console.error('Failed to process CSV:', csvError);
      setStatusMessage('Could not parse CSV file.');
    }
  };

  const startEditLearner = (learner) => {
    setEditingLearnerId(learner.id);
    setEditingLearner({
      surname: learner.surname || '',
      name: learner.name || '',
      grade: normalizeGrade(learner.grade),
      subjects: normalizeSubjects(learner.subjects),
      school: learner.school || '',
      project: learner.project || '',
    });
  };

  const cancelEditLearner = () => {
    setEditingLearnerId(null);
    setEditingLearner(null);
  };

  const saveEditedLearner = async () => {
    if (!editingLearnerId || !user?.uid || !editingLearner) return;
    const normalized = normalizeLearnerRow(editingLearner, user.uid);
    if (!validateLearner(normalized)) {
      setStatusMessage('Edited learner needs Surname, Name, Grade, and Subject.');
      return;
    }

    try {
      await updateLearner(editingLearnerId, normalized);
      cancelEditLearner();
      setStatusMessage('Learner updated.');
    } catch (updateError) {
      console.error('Failed to update learner:', updateError);
    }
  };

  const handleDeleteLearner = async (learnerId) => {
    try {
      await deleteLearner(learnerId);
      if (editingLearnerId === learnerId) cancelEditLearner();
      setStatusMessage('Learner deleted.');
    } catch (deleteError) {
      console.error('Failed to delete learner:', deleteError);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        <div className="relative overflow-hidden rounded-xl bg-gradient-to-r from-sigma-600 to-sigma-800 p-6 mb-6">
          <div className="relative z-10 flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => navigate('/document/new')}
                className="p-1.5 text-white/80 hover:text-white rounded-md hover:bg-white/10 transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-white flex items-center gap-2">
                  <Users className="w-6 h-6" />
                  Learners
                </h1>
                <p className="text-sigma-100 text-sm mt-0.5">
                  Add learners manually or upload CSV into the sigma-learners collection
                </p>
              </div>
            </div>
          </div>
          <div className="absolute -top-6 -right-6 w-32 h-32 bg-white/5 rounded-full" />
          <div className="absolute -bottom-8 -left-8 w-40 h-40 bg-white/5 rounded-full" />
        </div>

        <section className="rounded-xl border border-gray-200 bg-white shadow-sm p-4 sm:p-6 mb-6">
          <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Spreadsheet Entry</h2>
            <div className="flex items-center gap-2">
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,text/csv"
                className="hidden"
                onChange={handleCsvUpload}
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                <Upload className="w-4 h-4" />
                Upload CSV
              </button>
              <button
                type="button"
                onClick={handleDownloadTemplateCsv}
                className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                <Download className="w-4 h-4" />
                Download Template
              </button>
              <button
                type="button"
                onClick={addEntryRow}
                className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                <Plus className="w-4 h-4" />
                Add Row
              </button>
              <button
                type="button"
                onClick={handleSaveRows}
                disabled={!hasEntryData || isSaving}
                className="inline-flex items-center gap-2 rounded-lg bg-sigma-700 px-4 py-2 text-sm font-semibold text-white hover:bg-sigma-800 disabled:cursor-not-allowed disabled:bg-gray-300"
              >
                <Save className="w-4 h-4" />
                {isSaving ? 'Saving...' : 'Save Rows'}
              </button>
            </div>
          </div>

          <div className="overflow-x-auto border border-gray-200 rounded-lg">
            <table className="min-w-[980px] w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700">Surname</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700">Name</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700">Grade (4 - 12)</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700">Subjects</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700">School</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700">Project</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700 w-12" />
                </tr>
              </thead>
              <tbody>
                {entryRows.map((row) => (
                  <tr key={row.id} className="border-t border-gray-200 align-top">
                    <td className="px-2 py-2">
                      <input
                        value={row.surname}
                        onChange={(event) => updateEntryRow(row.id, 'surname', event.target.value)}
                        className="w-full rounded-md border border-gray-300 px-2 py-1.5 outline-none focus:border-sigma-500 focus:ring-2 focus:ring-sigma-100"
                      />
                    </td>
                    <td className="px-2 py-2">
                      <input
                        value={row.name}
                        onChange={(event) => updateEntryRow(row.id, 'name', event.target.value)}
                        className="w-full rounded-md border border-gray-300 px-2 py-1.5 outline-none focus:border-sigma-500 focus:ring-2 focus:ring-sigma-100"
                      />
                    </td>
                    <td className="px-2 py-2">
                      <select
                        value={row.grade}
                        onChange={(event) => updateEntryRow(row.id, 'grade', event.target.value)}
                        className="w-full rounded-md border border-gray-300 px-2 py-1.5 bg-white outline-none focus:border-sigma-500 focus:ring-2 focus:ring-sigma-100"
                      >
                        <option value="">Select</option>
                        {GRADE_OPTIONS.map((grade) => (
                          <option key={grade} value={grade}>
                            {grade}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="px-2 py-2">
                      <div className="space-y-1">
                        {SUBJECT_OPTIONS.map((subject) => (
                          <label key={subject} className="flex items-center gap-2 text-xs text-gray-700">
                            <input
                              type="checkbox"
                              checked={row.subjects.includes(subject)}
                              onChange={() => updateEntryRow(row.id, 'subjects', subject)}
                            />
                            {subject}
                          </label>
                        ))}
                      </div>
                    </td>
                    <td className="px-2 py-2">
                      <input
                        value={row.school}
                        onChange={(event) => updateEntryRow(row.id, 'school', event.target.value)}
                        className="w-full rounded-md border border-gray-300 px-2 py-1.5 outline-none focus:border-sigma-500 focus:ring-2 focus:ring-sigma-100"
                      />
                    </td>
                    <td className="px-2 py-2">
                      <input
                        value={row.project}
                        onChange={(event) => updateEntryRow(row.id, 'project', event.target.value)}
                        className="w-full rounded-md border border-gray-300 px-2 py-1.5 outline-none focus:border-sigma-500 focus:ring-2 focus:ring-sigma-100"
                      />
                    </td>
                    <td className="px-2 py-2">
                      <button
                        type="button"
                        onClick={() => removeEntryRow(row.id)}
                        title="Remove row"
                        className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-gray-300 text-gray-500 hover:text-red-700 hover:border-red-300"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="text-xs text-gray-500 mt-3">
            Required per learner: Surname, Name, Grade (4-12), and at least one Subject.
          </p>
        </section>

        <section className="rounded-xl border border-gray-200 bg-white shadow-sm p-4 sm:p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Saved Learners</h2>

          {isLoading ? (
            <div className="py-10 text-center">
              <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-sigma-600" />
            </div>
          ) : learners.length === 0 ? (
            <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-6 text-center text-sm text-gray-600">
              No learners saved yet.
            </div>
          ) : (
            <div className="overflow-x-auto border border-gray-200 rounded-lg">
              <table className="min-w-[980px] w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left font-semibold text-gray-700">Surname</th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-700">Name</th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-700">Grade</th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-700">Subjects</th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-700">School</th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-700">Project</th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-700 w-28">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {learners.map((learner) => {
                    const isEditing = editingLearnerId === learner.id && editingLearner;
                    return (
                      <tr key={learner.id} className="border-t border-gray-200 align-top">
                        <td className="px-2 py-2">
                          {isEditing ? (
                            <input
                              value={editingLearner.surname}
                              onChange={(event) =>
                                setEditingLearner((current) => ({ ...current, surname: event.target.value }))
                              }
                              className="w-full rounded-md border border-gray-300 px-2 py-1.5"
                            />
                          ) : (
                            learner.surname
                          )}
                        </td>
                        <td className="px-2 py-2">
                          {isEditing ? (
                            <input
                              value={editingLearner.name}
                              onChange={(event) =>
                                setEditingLearner((current) => ({ ...current, name: event.target.value }))
                              }
                              className="w-full rounded-md border border-gray-300 px-2 py-1.5"
                            />
                          ) : (
                            learner.name
                          )}
                        </td>
                        <td className="px-2 py-2">
                          {isEditing ? (
                            <select
                              value={editingLearner.grade}
                              onChange={(event) =>
                                setEditingLearner((current) => ({ ...current, grade: event.target.value }))
                              }
                              className="w-full rounded-md border border-gray-300 px-2 py-1.5 bg-white"
                            >
                              <option value="">Select</option>
                              {GRADE_OPTIONS.map((grade) => (
                                <option key={grade} value={grade}>
                                  {grade}
                                </option>
                              ))}
                            </select>
                          ) : (
                            learner.grade
                          )}
                        </td>
                        <td className="px-2 py-2">
                          {isEditing ? (
                            <div className="space-y-1">
                              {SUBJECT_OPTIONS.map((subject) => (
                                <label key={subject} className="flex items-center gap-2 text-xs text-gray-700">
                                  <input
                                    type="checkbox"
                                    checked={editingLearner.subjects.includes(subject)}
                                    onChange={() =>
                                      setEditingLearner((current) => {
                                        const hasSubject = current.subjects.includes(subject);
                                        return {
                                          ...current,
                                          subjects: hasSubject
                                            ? current.subjects.filter((item) => item !== subject)
                                            : [...current.subjects, subject],
                                        };
                                      })
                                    }
                                  />
                                  {subject}
                                </label>
                              ))}
                            </div>
                          ) : (
                            learner.subjects?.join(', ')
                          )}
                        </td>
                        <td className="px-2 py-2">
                          {isEditing ? (
                            <input
                              value={editingLearner.school}
                              onChange={(event) =>
                                setEditingLearner((current) => ({ ...current, school: event.target.value }))
                              }
                              className="w-full rounded-md border border-gray-300 px-2 py-1.5"
                            />
                          ) : (
                            learner.school
                          )}
                        </td>
                        <td className="px-2 py-2">
                          {isEditing ? (
                            <input
                              value={editingLearner.project}
                              onChange={(event) =>
                                setEditingLearner((current) => ({ ...current, project: event.target.value }))
                              }
                              className="w-full rounded-md border border-gray-300 px-2 py-1.5"
                            />
                          ) : (
                            learner.project
                          )}
                        </td>
                        <td className="px-2 py-2">
                          <div className="flex items-center gap-1">
                            {isEditing ? (
                              <>
                                <button
                                  type="button"
                                  onClick={saveEditedLearner}
                                  className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-gray-300 text-sigma-700 hover:bg-sigma-50"
                                  title="Save"
                                >
                                  <Save className="w-4 h-4" />
                                </button>
                                <button
                                  type="button"
                                  onClick={cancelEditLearner}
                                  className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-gray-300 text-gray-600 hover:bg-gray-100"
                                  title="Cancel"
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              </>
                            ) : (
                              <button
                                type="button"
                                onClick={() => startEditLearner(learner)}
                                className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-gray-300 text-gray-600 hover:bg-gray-100"
                                title="Edit"
                              >
                                <Pencil className="w-4 h-4" />
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={() => handleDeleteLearner(learner.id)}
                              className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-gray-300 text-gray-600 hover:text-red-700 hover:border-red-300"
                              title="Delete"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {(statusMessage || error) && (
          <div
            className={`mt-4 rounded-lg border px-4 py-3 text-sm ${
              error
                ? 'border-red-200 bg-red-50 text-red-700'
                : 'border-green-200 bg-green-50 text-green-700'
            }`}
          >
            {error || statusMessage}
          </div>
        )}
      </div>
    </div>
  );
};

export default LearnersPage;
